import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Link, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { createExchangeRate, deleteExchangeRate, getExchangeRates, syncExchangeRates } from '../../services/platform/localeService';
import type { SettingsContext, SettingsHistoryEntry } from '../../services/platform/settingsService';

type LocaleCtx = NonNullable<SettingsContext['locale']>;

interface Props {
  locale: LocaleCtx;
  draft: {
    base: string;
    display: string;
    settlement: string;
    reporting: string;
    autoConvert: boolean;
    provider: string;
    historical: boolean;
  };
  history: SettingsHistoryEntry[];
  onNotify: (severity: 'success' | 'error', message: string) => void;
}

const ROUNDING_LABELS: Record<string, string> = {
  half_up: 'round half up',
  half_down: 'round half down',
  bankers: "banker's rounding",
  floor: 'round down',
};

/** Same lookup order as the server: manual rates, then ECB (when selected), then the Currencies master. */
function effectiveRate(ctx: LocaleCtx, base: string, target: string, provider: string) {
  if (base === target) return { rate: 1, source: 'same', effectiveFrom: null as string | null, history: 0 };
  for (const source of provider === 'ecb' ? ['manual', 'ecb'] : ['manual']) {
    const direct = ctx.rates.find((r) => r.source === source && r.from === base && r.to === target);
    if (direct) return { rate: direct.rate, source, effectiveFrom: direct.effectiveFrom, history: direct.history };
    const inverse = ctx.rates.find((r) => r.source === source && r.from === target && r.to === base && r.rate > 0);
    if (inverse) return { rate: 1 / inverse.rate, source, effectiveFrom: inverse.effectiveFrom, history: inverse.history };
  }
  const from = ctx.currencies.find((c) => c.code === base);
  const to = ctx.currencies.find((c) => c.code === target);
  const usable = (c?: LocaleCtx['currencies'][number]) => !c || c.code === ctx.profileBase || provider === 'ecb' || c.masterRateSource !== 'api';
  const fromRate = base === ctx.profileBase ? 1 : from?.masterRate;
  const toRate = target === ctx.profileBase ? 1 : to?.masterRate;
  if (fromRate && toRate && usable(from) && usable(to)) return { rate: toRate / fromRate, source: 'master', effectiveFrom: null, history: 0 };
  return null;
}

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual rate',
  ecb: 'ECB reference rate',
  master: 'Currencies master',
  same: 'Same currency',
};

export default function LocaleCurrencyPanel({ locale, draft, history, onNotify }: Props) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ fromCurrency: draft.base, toCurrency: '', rate: '', effectiveFrom: today });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { data: storedRates = [] } = useQuery({ queryKey: ['exchange-rates'], queryFn: getExchangeRates });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    queryClient.invalidateQueries({ queryKey: ['settings'] });
    queryClient.invalidateQueries({ queryKey: ['settings-context'] });
  };
  const errorOf = (err: unknown) => (err as { response?: { data?: { message?: string; errors?: Record<string, string> } } })?.response?.data;

  const addMut = useMutation({
    mutationFn: () => createExchangeRate({ ...form, rate: form.rate.trim(), source: 'manual' }),
    onSuccess: () => {
      refresh();
      setFormErrors({});
      setForm((f) => ({ ...f, rate: '' }));
      onNotify('success', `Rate ${form.fromCurrency} → ${form.toCurrency} saved from ${form.effectiveFrom}`);
    },
    onError: (err) => {
      const data = errorOf(err);
      setFormErrors(data?.errors || {});
      onNotify('error', data?.message || 'Could not save the rate');
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteExchangeRate(id),
    onSuccess: () => { refresh(); onNotify('success', 'Rate removed'); },
    onError: (err) => onNotify('error', errorOf(err)?.message || 'Could not remove the rate'),
  });
  const syncMut = useMutation({
    mutationFn: () => syncExchangeRates(draft.base),
    onSuccess: (r) => { refresh(); onNotify('success', r.message || `Synced ${r.synced} rate(s)`); },
    onError: (err) => onNotify('error', errorOf(err)?.message || 'ECB sync failed'),
  });

  const meta = (code: string) => locale.currencies.find((c) => c.code === code);
  const roles = [
    { label: 'Base', code: draft.base, rule: 'Every price, payment, wallet balance and payout is calculated, charged and stored in it. Never converted.' },
    { label: 'Display', code: draft.display, rule: draft.display === draft.base ? 'Same as Base — prices are shown as charged.' : `Price quotes also show an estimate in ${draft.display} at today's rate; the customer is charged in ${draft.base}.` },
    { label: 'Settlement', code: draft.settlement, rule: `Partner payouts are wallet credits in ${draft.base}, so this must equal Base.` },
    { label: 'Reporting', code: draft.reporting, rule: draft.reporting === draft.base ? 'Same as Base — report amounts are not converted.' : `Report money columns are converted to ${draft.reporting} at ${draft.historical ? 'the rate effective on each row\'s transaction date' : 'today\'s rate'}.` },
  ];
  const needed = [...new Set([draft.display, draft.reporting].filter((c) => c && c !== draft.base))];
  const sync = locale.fxSync;
  const currencyOptions = locale.currencies.map((c) => c.code);

  return (
    <Alert severity={needed.some((c) => !effectiveRate(locale, draft.base, c, draft.provider)) ? 'warning' : 'info'} sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Chip size="small" color="primary" label={`Charged & stored in ${draft.base}`} />
        <Chip size="small" variant="outlined" color={needed.length && !draft.autoConvert ? 'warning' : 'default'} label={needed.length ? `Auto Convert ${draft.autoConvert ? 'on' : 'off'} · converts to ${needed.join(', ')}` : 'No conversion needed — all currencies are ' + draft.base} />
        <Chip size="small" variant="outlined" label={`Rates: ${draft.provider === 'ecb' ? 'ECB (daily) + manual overrides' : 'manual'}`} />
        {draft.provider === 'ecb' && (
          <Chip size="small" variant="outlined" color={sync?.ok ? 'success' : 'warning'} label={sync ? `${sync.ok ? 'Last ECB sync' : 'ECB sync failed'} ${new Date(sync.at).toLocaleString()}` : 'ECB not synced yet'} />
        )}
      </Box>

      <Typography variant="subtitle2">What each currency does</Typography>
      <Table size="small" sx={{ mb: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Role</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Conversion rule</TableCell>
            <TableCell>Rounding</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((r) => {
            const m = meta(r.code);
            return (
              <TableRow key={r.label}>
                <TableCell>{r.label}</TableCell>
                <TableCell>{r.code}{m ? ` (${m.symbol})` : ''}</TableCell>
                <TableCell><Typography variant="caption">{r.rule}</Typography></TableCell>
                <TableCell><Typography variant="caption">{m ? `${m.decimalPlaces} decimals, ${ROUNDING_LABELS[m.roundingRule] || m.roundingRule}` : '—'}</Typography></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Typography variant="subtitle2">Exchange rates in use</Typography>
      {needed.length ? (
        <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
          {needed.map((code) => {
            const r = effectiveRate(locale, draft.base, code, draft.provider);
            return (
              <li key={code}>
                <Typography variant="caption" color={r ? 'text.primary' : 'error.main'}>
                  {r
                    ? `1 ${draft.base} = ${Number(r.rate.toPrecision(8))} ${code} · ${SOURCE_LABELS[r.source]}${r.effectiveFrom ? ` since ${new Date(r.effectiveFrom).toLocaleDateString()}` : ''}${r.history > 1 ? ` · ${r.history} dated rates stored` : ''}`
                    : `No ${draft.base} → ${code} rate — add one below${draft.provider === 'ecb' ? ' or run Sync now' : ''}`}
                </Typography>
              </li>
            );
          })}
        </Box>
      ) : (
        <Typography variant="caption" display="block" sx={{ mb: 1 }}>None needed while Display and Reporting equal {draft.base}.</Typography>
      )}

      <Typography variant="subtitle2" sx={{ mt: 1 }}>Rates you have entered</Typography>
      {storedRates.filter((r) => r.source === 'manual').length ? (
        <Table size="small" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Pair</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Applies from</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {storedRates.filter((r) => r.source === 'manual').slice(0, 20).map((r) => (
              <TableRow key={r._id}>
                <TableCell>{r.fromCurrency} → {r.toCurrency}</TableCell>
                <TableCell>{r.rate}</TableCell>
                <TableCell>{new Date(r.effectiveFrom).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Remove this rate">
                    <span>
                      <IconButton size="small" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(r._id)}><DeleteOutline fontSize="small" /></IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="caption" display="block" sx={{ mb: 1 }}>No manual rates yet.</Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }} sx={{ mb: 1.5 }}>
        <TextField select size="small" label="From" value={form.fromCurrency} onChange={(e) => setForm({ ...form, fromCurrency: e.target.value })} error={!!formErrors.fromCurrency} helperText={formErrors.fromCurrency} sx={{ minWidth: 100 }}>
          {currencyOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="To" value={form.toCurrency} onChange={(e) => setForm({ ...form, toCurrency: e.target.value })} error={!!formErrors.toCurrency} helperText={formErrors.toCurrency} sx={{ minWidth: 100 }}>
          {currencyOptions.filter((c) => c !== form.fromCurrency).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          label={`1 ${form.fromCurrency || '…'} =`}
          value={form.rate}
          onChange={(e) => setForm({ ...form, rate: e.target.value })}
          error={!!formErrors.rate}
          helperText={formErrors.rate || (form.toCurrency ? `${form.toCurrency} — positive, up to 8 decimals` : 'positive, up to 8 decimals')}
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField size="small" type="date" label="Applies from" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} error={!!formErrors.effectiveFrom} helperText={formErrors.effectiveFrom} InputLabelProps={{ shrink: true }} />
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: { sm: 0.5 } }}
          disabled={addMut.isPending || !form.toCurrency || !form.rate.trim()}
          startIcon={addMut.isPending ? <CircularProgress size={14} /> : undefined}
          onClick={() => addMut.mutate()}
        >
          Add rate
        </Button>
        {draft.provider === 'ecb' && (
          <Button size="small" sx={{ mt: { sm: 0.5 } }} disabled={syncMut.isPending} startIcon={syncMut.isPending ? <CircularProgress size={14} /> : undefined} onClick={() => syncMut.mutate()}>
            Sync ECB now
          </Button>
        )}
      </Stack>

      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        <li><strong>Auto Convert</strong> is only needed (and only allowed to matter) when Display or Reporting differs from Base, and then a rate must exist for each of those currencies — saving is blocked otherwise. With every currency set to {draft.base} it has no effect.</li>
        <li><strong>Rate lookup:</strong> a manual rate always wins; with ECB selected, ECB reference rates are used next (fetched daily, and immediately when you switch to ECB); the Currencies master rate is the last fallback. A reverse rate (e.g. USD → {draft.base}) is inverted automatically.</li>
        <li><strong>Historical FX</strong> keeps every rate with the date it applies from. Reports then convert each row at the rate in force on its transaction date; dates before the first stored rate use the earliest fallback rate. Enter dated manual rates (or use ECB, which adds a dated rate every day) to build history.</li>
        <li><strong>Rounding:</strong> converted amounts use the target currency&apos;s decimals and rounding rule from the <Link component={RouterLink} to="/config/masters?type=currencies">Currencies master</Link>.</li>
        <li><strong>When changes apply:</strong> new price quotes and any report you run after saving. Bookings, payments, wallet balances and payouts already recorded keep their amounts and currency. A new rate applies from its &quot;Applies from&quot; date.</li>
        <li>Currencies, rates and the ECB sync are stored per tenant and only affect this tenant.{locale.countryOverrides ? ` ${locale.countryOverrides} country-specific display/reporting override(s) also apply (Config → Locale).` : ''}</li>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Recent changes</Typography>
      {history.length ? (
        <Stack spacing={0.25}>
          {history.map((h) => (
            <Typography key={h.id} variant="caption">
              {new Date(h.at).toLocaleString()} · {h.by}: {h.changes.map((c) => `${c.label} ${c.from ?? 'default'} → ${c.to}`).join('; ') || 'settings updated'}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" display="block">No currency settings saved for this tenant yet.</Typography>
      )}
    </Alert>
  );
}
